import React, { useEffect, useMemo, useState } from 'react';
import { COLOR_ACCENT_2, COLOR_BLUE_BLACK_LIGHT, exchangeRates } from 'src/utils/const';
import { ProductIDOptions } from 'src/utils/stripeConfig';

import { loadStripe, Stripe } from '@stripe/stripe-js';
import { CardElement, Elements, useStripe, useElements } from '@stripe/react-stripe-js';

import { Alert } from '@material-ui/lab';
import { Typography, TextField, Grid, Button, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LockSharpIcon from '@material-ui/icons/LockSharp';
import { PaymentPropTypes } from 'src/interfaces/iproptypes';
import { callApi } from 'src/core/callApi';
import Link from '@mui/material/Link';
import { formatCurrencyBasedOnCountryCode, sleep } from 'src/utils/functions';
import { Box } from '@mui/material';
import { logAmplitudeEvent } from 'src/utils/analytics';
import { stripeConfig } from 'src/utils/stripeConfig';

const moment = require('moment');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
};

const useStyles = makeStyles(theme => ({
  paymentTitle: {
    marginBottom: 20,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  paymentDetailTitle: {
    marginBottom: 7,
  },
  promoCodeTitle: {
    marginBottom: 7,
    marginTop: 24,
  },
  paymentButton: {
    background: COLOR_ACCENT_2,
    borderRadius: 5,
    border: 0,
    color: 'white',
    height: 35,
    width: '100%',
    padding: '0 15px',
    marginBottom: '6px',
    marginTop: '30px',
    boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
  },
  emailField: {
    marginBottom: theme.spacing(4),
    width: '100%',
  },
  stripeLogo: {
    width: 180,
    height: 'auto',
    padding: 0,
  },
}));

const CheckoutForm = ({
  success,
  fail,
  countryCode,
  city,
  district,
  productID,
  product,
  duration,
  promoCode,
  discountedPrice,
  countryToUnlock,
  // countryTier,
  // isDisabled,
  subscriptionPriceAccordingToUserLocalCurrency,
  userLocalCurrency,
  isTrial,
  isTest,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const cardElement = elements?.getElement(CardElement);
  const classes = useStyles();
  
  // Pre-fill email from localStorage if available
 
  
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  let affiliateKey = localStorage.getItem('affiliatedKey');



  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  /*
    This is the code where we are calling the backend to create subscription.
  */
  const getSubscriptionAndPaymentIntent = async (
    paymentMethod,
    customerID = '',
    subscriptionID = ''
  ) => {
    const { id } = paymentMethod;
    const url =
      'https://w5q8qnl6i1.execute-api.us-east-2.amazonaws.com/prod/createStripeSubscription';
    const jwt = localStorage.getItem('auth-token');
    const response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      body: JSON.stringify({
        email: email,
        payID: id,
        countryCode: countryCode,
        city: city,
        district: district,
        productID: productID,
        productTitle: product,
        duration: duration,
        jwt: jwt,
        promoCode: promoCode,
        customerID: customerID,
        subscriptionID: subscriptionID,
        country_to_unlock: countryToUnlock,
        currency: userLocalCurrency,
        affiliateKey: affiliateKey,
        isTrial: isTrial,
        ...(isTest && { isTest: true }),
      }),
    });
    const apiStatus = response.status;
    if (apiStatus === 400) {
      response.json().then(message => 'error' in message && fail(message.error));
      return { apiStatus, subscription: null, paymentIntent: null };
    }
    const responseJson = await response.json();
    const subscription = apiStatus === 200 ? responseJson.payment_res : responseJson;
    const paymentIntent =
      subscription && subscription.latest_invoice && subscription.latest_invoice.payment_intent;
    return { apiStatus, subscription, paymentIntent };
  };

  const waitForPaymentProcessing = async (
    stripe,
    paymentIntent,
    maxRetries = 5,
    delayMs = 3000
  ) => {
    const clientSecret = paymentIntent.client_secret;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      await sleep(delayMs);

      const { paymentIntent: updatedIntent } = await stripe.retrievePaymentIntent(clientSecret);
      const status = updatedIntent.status;

      console.log(`Attempt ${attempt}: PaymentIntent status is ${status}`);

      if (status === 'succeeded') {
        return true;
      } else if (status === 'processing') {
        // Keep retrying
        continue;
      } else {
        // Any other status is treated as failure
        return false;
      }
    }

    // If retries are exhausted and still processing, consider as failure
    return false;
  };

  const confirmCardPaymentRecursively = async (stripe, paymentIntent) => {
    const clientSecret = paymentIntent.client_secret;
    const confirmCardPaymentResp = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,

      },
    });
    const confirmStatus =
      confirmCardPaymentResp &&
      confirmCardPaymentResp.paymentIntent &&
      confirmCardPaymentResp.paymentIntent.status;

    if (confirmStatus === 'requires_action') {
      return confirmCardPaymentRecursively(stripe, paymentIntent);
    }

    if (confirmStatus === 'processing') {
      return waitForPaymentProcessing(stripe, paymentIntent);
    }

    if (confirmStatus === 'succeeded') {
      return true;
    }

    return false;
  };

  const getSubscriptionStatus = async (paymentMethod, customerID, subscriptionID) => {
    const { apiStatus, subscription, paymentIntent } = await getSubscriptionAndPaymentIntent(
      paymentMethod,
      customerID,
      subscriptionID
    );
    if (apiStatus !== 200) {
      return false;
    }
    return true;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    if (!stripe || !cardElement) {
      setLoading(false);
      return;
    }
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        email: email, // Pass email to help Stripe Link match saved payment methods
      },
    });

    if (error || !paymentMethod) {
      fail();
      setLoading(false);
      return;
    }

    const { apiStatus, subscription, paymentIntent } = await getSubscriptionAndPaymentIntent(
      paymentMethod
    );
    if (apiStatus === 400) {
      fail();
      setLoading(false);
      return;
    }
    const customerID = subscription.customer;
    const subscriptionID = subscription.id;

    if (apiStatus === 200) {
      // Success
      success();
      setLoading(false);
      return;
    }

    const paymentIntentStatus: string = paymentIntent.status;
    // at this point, we have a payment intent that is either requires_action or requires_payment_method or processing
    if (paymentIntentStatus === 'processing') {
      const isPaymentSuccess = await waitForPaymentProcessing(stripe, paymentIntent);
      if (!isPaymentSuccess) {
        fail();
        setLoading(false);
        return;
      }
      const isSubscriptionSuccess = await getSubscriptionStatus(
        paymentMethod,
        customerID,
        subscriptionID
      );
      if (!isSubscriptionSuccess) {
        fail();
        setLoading(false);
        return;
      }
      success();
      setLoading(false);
    }

    if (paymentIntentStatus != 'requires_action') {
      // Error - Payment failed
      fail();
      setLoading(false);
      return;
    }

    // Additional action required (eg: 3D Secure)
    const isAdditionalActionSuccess = await confirmCardPaymentRecursively(stripe, paymentIntent);
    if (!isAdditionalActionSuccess) {
      // Error - Payment failed
      fail();
      setLoading(false);
      return;
    }

    const isSubscriptionActive = await getSubscriptionStatus(
      paymentIntent,
      customerID,
      subscriptionID
    );
    if (!isSubscriptionActive) {
      // Error - Payment failed
      fail();
      setLoading(false);
      return;
    }
    success();
    setLoading(false);
  };

  return (
    <Grid container direction="column" justifyContent="space-evenly">
      {loading && (
        <Grid style={{ marginBottom: '20px' }}>
          <Alert severity="warning">
            <Typography variant="body2">
              Please do not close this page or window — your payment is still being processed.
            </Typography>
          </Alert>
        </Grid>
      )}

      <Grid>
        <Grid className={classes.paymentTitle}>
          {!isTrial && (
            <Typography variant="h5" style={{ fontWeight: 'bold', color: COLOR_BLUE_BLACK_LIGHT }}>
              Payment details
            </Typography>
          )}
          <Box sx={{ padding: '5px', border: '1px solid grey', borderRadius: '5px' }}>
            <Typography variant="body1">
              Powered by <span style={{ fontWeight: 500 }}>Stripe</span>
            </Typography>
          </Box>
        </Grid>
        <form 
          style={{ width: '100%' }} 
          onSubmit={handleSubmit}
        >
          <Grid className={classes.paymentDetailTitle}>
            <Typography variant="caption" color="textPrimary">
              Email
            </Typography>
          </Grid>
          <Grid>
            <TextField
              required
              placeholder="Billing Email"
              type="email"
              className={classes.emailField}
              onChange={handleEmailChange}
            />
          </Grid>
          <Grid className={classes.paymentDetailTitle}>
            <Typography variant="caption" color="textPrimary">
              Card information
            </Typography>
          </Grid>
          <Grid>
            <CardElement options={CARD_ELEMENT_OPTIONS} />
            {isTrial ? (
              <Button
                type="submit"
                disabled={loading}
                className={classes.paymentButton}
                size="large"
              >
                {loading && <CircularProgress />}
                Start trial
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={loading}
                className={classes.paymentButton}
                size="large"
              >
                {loading && <CircularProgress />}
                <LockSharpIcon style={{ fontSize: 15 }} /> &nbsp; Pay{' '}
                {exchangeRates[userLocalCurrency].symbol}
                {formatCurrencyBasedOnCountryCode(
                  discountedPrice
                    ? discountedPrice
                    : formatCurrencyBasedOnCountryCode(
                        subscriptionPriceAccordingToUserLocalCurrency[product][duration],
                        userLocalCurrency
                      ),
                  userLocalCurrency
                )}
                {'   (' + product.toUpperCase() + ')'}
              </Button>
            )}

            <Grid>
              {isTrial ? (
                <Typography variant="caption" color="textPrimary">
                  After your trial ends, you will be charged{' '}
                  {exchangeRates[userLocalCurrency].symbol}
                  {formatCurrencyBasedOnCountryCode(
                    discountedPrice
                      ? discountedPrice
                      : formatCurrencyBasedOnCountryCode(
                          subscriptionPriceAccordingToUserLocalCurrency[product][duration],
                          userLocalCurrency
                        ),
                    userLocalCurrency
                  )}{' '}
                  per year starting{' '}
                  {moment()
                    .add(15, 'days')
                    .format('MMMM D, YYYY')}
                  . You can always cancel before then.
                </Typography>
              ) : (
                <Typography variant="caption" color="textPrimary">
                  {' '}
                  You will be charged {duration} until you choose to cancel.{' '}
                  {duration === 'yearly' ? 'You will be billed today. ' : ''}There are no contracts
                  or commitments. Your payment information is secure. Powered by Stripe.{' '}
                </Typography>
              )}
            </Grid>
            <Grid>
              <Typography variant="caption" color="textPrimary" style={{ fontStyle: 'italic' }}>
                {' '}
                By submitting this form, you confirm that you agree to our{' '}
                <Link href="https://airbtics.com/support/" target="_blank">
                  Terms of Service
                </Link>
              </Typography>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
};


const Payment = ({
  countryCode,
  city,
  district,
  product,
  duration,
  promoCode,
  setSubscriptionModalOpen,
  discountedPrice,
  countryToUnlock,
  // countryTier,
  subscriptionPriceAccordingToUserLocalCurrency,
  userLocalCurrency,
  isTrial,
  stripeAccount,
}: PaymentPropTypes) => {
  /*
    STRIPE TEST CODE 
    This will use the test card from Stripe with the test key
  */
  // const isTest: boolean = true;
  const isTest: boolean = false;

  // 1) Dynamic productID
  const productID = useMemo(() => {
    return isTest
      ? stripeConfig[stripeAccount].testProductIDOptions[product].productID[duration]
      : stripeConfig[stripeAccount].productIDOptions[product].productID[duration];
  }, [isTest, stripeAccount, product, duration]);

  // 2) Dynamic publishable key (string)
  const stripePromiseKey = useMemo(() => {
    return isTest
      ? stripeConfig[stripeAccount].promiseKeyTest
      : stripeConfig[stripeAccount].promiseKey;
  }, [isTest, stripeAccount]);

  // 3) Dynamic Stripe Promise
  const stripePromise = useMemo<Promise<Stripe | null>>(() => {
    return loadStripe(stripePromiseKey);
  }, [stripePromiseKey]);

  const [alertMessage, setAlertMessage] = useState('');

  const [status, setStatus] = React.useState('ready');
  if (status === 'success') {
    setSubscriptionModalOpen(false);
    window.location.reload();
  }

  return (
    <>
      {status === 'fail' && (
        <Alert severity="error" style={{ marginBottom: '20px' }}>
          We encountered an issue processing your payment. Please try again, or contact us at support@airbtics.com and we'll help you complete your subscription right away.
          {alertMessage && ` ${alertMessage}`}
        </Alert>
      )}
      <Elements stripe={stripePromise} key={stripeAccount}>
        <CheckoutForm
          countryCode={countryCode}
          city={city}
          district={district}
          productID={productID}
          product={product}
          duration={duration}
          promoCode={promoCode}
          // isDisabled={product === 'global' && countryToUnlock === null ? true : false}
          success={() => {
            logAmplitudeEvent('subscribed', {
              plan_name: product,
              plan_duration: duration,
              is_upgrade: false,
            });
            setStatus('success');
          }}
          fail={(message: string | undefined) => {
            setStatus('fail');
            if (message) {
              setAlertMessage(message);
            }
          }}
          discountedPrice={discountedPrice}
          countryToUnlock={countryToUnlock}
          // countryTier={countryTier}
          subscriptionPriceAccordingToUserLocalCurrency={
            subscriptionPriceAccordingToUserLocalCurrency
          }
          userLocalCurrency={userLocalCurrency}
          isTrial={isTrial}
          isTest={isTest}
        />
      </Elements>
    </>
  );
};

export default Payment;
